import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Menu, X, Info, Star, Award, Heart, Building2 } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  Tuanson Construction — concept site. Corporate contractor identity built
  around the company's own navy/gold branding and real community outreach
  photography. PCAB classification is unconfirmed and intentionally not claimed.
*/

const NAVY = "#0F2C4C";
const GOLD = "#C9A227";
const CREAM = "#F6F5F0";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/Tuansoncons/";
const NAME = "Tuanson Construction";
const SLUG = "tuanson-construction-concept";
const P = "/construction/tuanson";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function TuansonConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Capabilities", "#capabilities"], ["Public Projects", "#public-projects"], ["Community", "#community"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Mulish', sans-serif" }} data-testid="tuanson-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;600;700;800&family=Bitter:wght@500;600;700&display=swap');
        #top h1, #top h2, #top h3 { font-family: 'Bitter', serif; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#081B30] text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.2)]" : ""}`} style={{ background: NAVY }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="tu-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.jpg`} alt={NAME} className="h-11 w-11 rounded-full object-cover" />
            <span className="text-lg text-white font-extrabold tracking-tight">TUANSON</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-semibold text-white/75 hover:text-white">{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="tu-header-cta" className="hidden sm:inline-flex rounded-sm px-5 py-2.5 text-sm font-bold" style={{ background: GOLD, color: NAVY }}>
            Request a Quotation
          </a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="tu-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: NAVY }}>
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-white font-semibold">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="tu-mobile-cta" className="block mt-2 text-center rounded-sm px-5 py-3 text-sm font-bold" style={{ background: GOLD, color: NAVY }}>Request a Quotation</a>
          </div>
        )}
      </header>

      {/* HERO — restrained (limited completed-project photography) */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: NAVY }} data-testid="tu-hero">
        <img src={`${P}/onsite-install.jpg`} alt="A verified on-site installation by Tuanson Construction" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "70% center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,rgba(13,49,79,.97) 0%,rgba(13,49,79,.82) 52%,rgba(13,49,79,.2) 86%)" }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>Talisay City, Cebu &middot; General Building &amp; Engineering</span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-white font-extrabold">Building Cebu's Homes, Facilities &amp; Public Works.</h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-2xl">
              Site development, residential, commercial, warehouse and public-infrastructure construction across Cebu.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="tu-hero-cta" className="rounded-sm px-7 py-3.5 text-sm font-bold" style={{ background: GOLD, color: NAVY }}>Request a Quotation</a>
              <a href="#capabilities" data-testid="tu-hero-capabilities" className="rounded-sm border border-white/40 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors">Our Capabilities</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>What We Do</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-snug">Capabilities</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10">
          {[
            [Building2, "Site Development", "Land and site development work as the first stage of a larger construction project."],
            [Award, "Residential & Commercial Builds", "Residential, commercial and warehouse construction for private and public-sector clients."],
            [Star, "On-Site Installations", "Structures such as gates, canopies and outdoor fixtures installed as part of a build."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: GOLD }} />
              <h3 className="mt-4 text-lg font-bold">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
        <motion.div {...fadeUp} className="mt-14 max-w-2xl mx-auto rounded-sm overflow-hidden" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
          <img src={`${P}/onsite-install.jpg`} alt="An on-site installation by Tuanson Construction" className="w-full h-72 object-cover" loading="lazy" />
          <p className="px-5 py-3 text-xs bg-white" style={{ color: `${INK}70` }}>An on-site installation from a recent residential project.</p>
        </motion.div>
      </section>

      {/* PUBLIC PROJECT RECORD */}
      <section id="public-projects" className="py-20 md:py-28" style={{ background: "#F0E9D7" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: NAVY }}>Documented Public Work</span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-snug">Projects Across Cebu</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["Road Concreting", "Sitio Mapa–Sitio Mangabon and Upper Kalunasan projects recorded by Cebu City."],
              ["Civic Renovation", "Repair and renovation of the 9th Floor Cebu City Executive Building."],
              ["Education Infrastructure", "A four-storey, 20-classroom project for Punta Princesa Elementary School."],
            ].map(([title, body]) => (
              <motion.article key={title} {...fadeUp} className="bg-white p-6 border-t-4" style={{ borderColor: GOLD }}>
                <h3 className="text-xl font-bold">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{body}</p>
              </motion.article>
            ))}
          </div>
          <motion.p {...fadeUp} className="mt-8 text-sm" style={{ color: `${INK}AA` }}>The Bitoon–Panlaan road project received an 89.92% “Very Satisfactory” final performance rating in a DPWH record.</motion.p>
        </div>
      </section>

      {/* COMMUNITY */}
      <section id="community" className="py-20 md:py-28" style={{ background: NAVY }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...fadeUp}>
            <Heart size={28} className="mx-auto" style={{ color: GOLD }} />
            <h2 className="mt-5 text-3xl sm:text-4xl text-white font-extrabold leading-snug">Rooted in the Community We Build In</h2>
            <p className="mt-4 text-white/75 leading-relaxed max-w-2xl mx-auto">Our team has taken part in local community relief efforts in Talisay City — because the neighborhoods we build in are the same ones we live in.</p>
          </motion.div>
          <motion.div {...fadeUp} className="mt-10 max-w-2xl mx-auto rounded-sm overflow-hidden">
            <img src={`${P}/team-community.jpg`} alt="Tuanson Construction team taking part in a community relief effort" className="w-full h-72 object-cover" loading="lazy" />
          </motion.div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: NAVY }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl font-extrabold leading-snug">Request a Quotation</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: NAVY }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>162 P. Labuca St., Kimba, Cansojong, Talisay City, Cebu</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone size={20} style={{ color: NAVY }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Phone</div><a href="tel:+63324918243" className="hover:underline">(032) 491 8243</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={20} style={{ color: NAVY }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Email</div><a href="mailto:ric_tuanson@tuansoncons.com" className="hover:underline">ric_tuanson@tuansoncons.com</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: NAVY }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="tu-facebook-link" className="hover:underline">facebook.com/tuansonconstruction</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="tu"
              accent={NAVY}
              accentText="#FFFFFF"
              heading="Request a Quotation"
              subheading="Site development, residential, commercial or warehouse — tell us about the project and we'll follow up."
              projectTypes={["Site Development", "Residential Construction", "Commercial / Warehouse", "Other"]}
              phone="+63324918243"
              phoneDisplay="(032) 491 8243"
              messengerUrl={FB}
              email="ric_tuanson@tuansoncons.com"
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: "#081B30", color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 rounded-full object-cover" />
              <span className="text-white text-lg font-extrabold">TUANSON CONSTRUCTION</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Contractor based in Talisay City, Cebu.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">162 P. Labuca St., Kimba,<br />Cansojong, Talisay City, Cebu</p>
            <p className="text-sm mt-2">(032) 491 8243</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 rounded-sm px-6 py-3.5 text-center text-sm font-bold shadow-xl" style={{ background: GOLD, color: NAVY }}>
        Message {NAME}
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
